import { useState } from "react";
import { User, Lock, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function AccountPage() {
  const { user } = useAuth();
  const { toast } = useToast();

  // --- Change Email ---
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);

  // --- Change Password ---
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // --- Delete Account ---
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handleChangeEmail = async () => {
    if (!newEmail.trim()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;
      toast({
        title: "Confirmation sent",
        description: `A confirmation link has been sent to ${newEmail.trim()}. Please check your inbox to complete the change.`,
      });
      setNewEmail("");
      setChangeEmailOpen(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setEmailLoading(false);
  };

  const handleSendPasswordResetLink = async () => {
    const email = resetPasswordEmail.trim();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });
      if (error) throw error;
      toast({
        title: "Reset link sent",
        description: `A password reset link has been sent to ${email}. Check your inbox to continue.`,
      });
      setResetPasswordOpen(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setPasswordLoading(false);
  };

  const openChangeEmailModal = () => {
    setNewEmail(user?.email || "");
    setChangeEmailOpen(true);
  };

  const openResetPasswordModal = () => {
    setResetPasswordEmail(user?.email || "");
    setResetPasswordOpen(true);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      if (error) {
        const status = (error as any).context?.status;
        if (status === 401) {
          // Session no longer valid (e.g. account already deleted) — just sign out.
          await supabase.auth.signOut();
          window.location.href = "/login";
          return;
        }
        let message = error.message;
        try {
          const body = await (error as any).context?.json?.();
          if (body?.error) message = body.error;
        } catch { /* keep generic message */ }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);
      await supabase.auth.signOut();
      window.location.href = "/login";
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to delete account", variant: "destructive" });
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <User className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
          <p className="text-sm text-muted-foreground">Manage your account security and email preferences</p>
        </div>
      </div>

      {/* Email Section */}
      <section className="border-y border-border py-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Email Address</h2>
          <p className="text-sm text-muted-foreground">
            Current email: <span className="font-medium text-foreground">{user?.email}</span>
          </p>
        </div>
        <Button
          variant="outline"
          onClick={openChangeEmailModal}
          className="w-full sm:w-auto mt-4"
        >
          Change Email Address
        </Button>
      </section>

      {/* Password Section */}
      <section className="border-b border-border py-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
            <Lock className="w-5 h-5" /> Change Password
          </h2>
          <p className="text-sm text-muted-foreground">
            Send a secure reset link to your email to set a new password
          </p>
        </div>
        <Button
          variant="outline"
          onClick={openResetPasswordModal}
          className="w-full sm:w-auto mt-4"
        >
          Reset Password
        </Button>
      </section>

      {/* Delete Account Section */}
      <section className="py-6 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">Delete Account</h2>
          <p className="text-sm text-muted-foreground">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="w-full sm:w-auto gap-2 mt-4">
              <Trash2 className="w-4 h-4" />
              Delete My Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">This will permanently delete your account, remove your access to all churches, and erase your profile data. This action <strong>cannot be undone</strong>.</span>
                <span className="block mt-3">Type <strong>DELETE</strong> to confirm:</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="mt-2"
            />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteConfirmText !== "DELETE" || deleteLoading}
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteAccount();
                }}
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      {/* Change Email Modal */}
      <Dialog open={changeEmailOpen} onOpenChange={setChangeEmailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Enter your new email address. We'll send a verification code to confirm the change.
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-email" className="text-sm font-medium">Email address</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Email address"
                className="w-full"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setChangeEmailOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleChangeEmail}
                disabled={emailLoading || !newEmail.trim()}
                className="bg-black text-white hover:bg-black/90"
              >
                {emailLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send verification email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              We&apos;ll send a secure password reset link to your email address.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-password-email" className="text-sm font-medium">Email address</Label>
              <Input
                id="reset-password-email"
                type="email"
                value={resetPasswordEmail}
                onChange={(e) => setResetPasswordEmail(e.target.value)}
                placeholder="Email address"
                className="w-full"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setResetPasswordOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSendPasswordResetLink}
                disabled={passwordLoading || !resetPasswordEmail.trim()}
                className="bg-black text-white hover:bg-black/90"
              >
                {passwordLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send reset link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
